/**
 * InovxLoader — global localization / resource loader.
 *
 * This is a modern ES-module port of the original GlobalLoader.ts IIFE.
 * It uses i18next (current API) + i18next-http-backend for async locale loading
 * and PubSub for event broadcasting, matching the original contract.
 */
import i18next from 'i18next';
import HttpBackend from 'i18next-http-backend';
import PubSub from 'pubsub-js';

// ── Events ──────────────────────────────────────────────────────────
const _CHANGE  = 'loader/change';
const _LOADED  = 'loader/loaded';
const _LOADING = 'loader/loading';

// ── Internal state ──────────────────────────────────────────────────
let _hasInitialized = false;
let _initializationPromise: Promise<void> | null = null;
let _languageChangePromise: Promise<void> | null = null;
const _loadStack: number[] = [];
const _functionStack: Array<() => void> = [];
const _namespaces = new Set<string>(['common']);

let _localeData: {
    lng: string;
    datePattern: string;
    timePattern: string;
    tz: string;
} = {
    lng: 'en-US',
    datePattern: 'MM/dd/yyyy',
    timePattern: 'hh:mm a',
    tz: '',
};

// ── Helpers ─────────────────────────────────────────────────────────
function _publishEvent(event: string) {
    PubSub.publish(event, undefined);
}

function _loadFinished() {
    _loadStack.pop();
    if (_loadStack.length === 0) {
        _publishEvent(_LOADED);
    }
}

function _enqueueLoad() {
    _loadStack.push(1);
    _publishEvent(_LOADING);
}

function normalizeLanguageCode(lng: string): string {
    const lngLower = (lng || '').toLowerCase();
    if (lngLower === "zh-hans" || lngLower === "zh-cn" || lngLower === "zh-hans-cn" || lngLower === "zh-sg" || lngLower === "zh-hans-sg") {
        return 'zh-Hans';
    } else if (lngLower === "ja" || lngLower.indexOf("ja-") >= 0) {
        return 'ja-JP';
    } else {
        return 'en-US';
    }
}

function getLocaleResourceLanguage(lng: string): string {
    switch ((lng || '').toLowerCase()) {
        case 'en-us': return 'en-US';
        case 'ja-jp': return 'ja-jp';
        case 'zh-hans': return 'zh-hans';
        default: return lng;
    }
}

function getStaticAssetRoot(): string {
    const appUrl = (window as any).APP_URL || (window as any).BASE_APP_PATH || '';
    return appUrl.replace(/\/$/, '');
}

function getLocaleLoadPath(lngs: string | string[], namespaces: string | string[], contentVersion: string): string {
    const lng = Array.isArray(lngs) ? lngs[0] : lngs;
    const namespace = Array.isArray(namespaces) ? namespaces[0] : namespaces;
    return `${getStaticAssetRoot()}/locale/${getLocaleResourceLanguage(lng)}/${namespace}.json?v=${contentVersion}`;
}

function updateI18nextLanguage(): Promise<void> {
    if (!_hasInitialized) {
        return _initializationPromise
            ? _initializationPromise.then(() => updateI18nextLanguage())
            : Promise.resolve();
    }

    const currentLanguage = typeof i18next.language === 'string' ? i18next.language : '';
    if (currentLanguage.toLowerCase() === _localeData.lng.toLowerCase()) {
        _publishEvent(_CHANGE);
        return Promise.resolve();
    }

    _enqueueLoad();
    _languageChangePromise = new Promise((resolve) => {
        i18next.changeLanguage(_localeData.lng, () => {
            i18next.reloadResources(_localeData.lng, Array.from(_namespaces), () => {
                _publishEvent(_CHANGE);
                _loadFinished();
                resolve();
            });
        });
    });

    return _languageChangePromise;
}

// ── Public API ──────────────────────────────────────────────────────

function setLocaleData(lng: string, datePattern: string, timePattern: string, tz: string): Promise<void> {
    _localeData = {
        lng: normalizeLanguageCode(lng),
        datePattern,
        timePattern,
        tz,
    };

    return updateI18nextLanguage();
}

function getLocaleData() {
    return _localeData;
}

/**
 * Initialize i18next with the http-backend.
 * The backend loads JSON files from /locale/{{lng}}/{{ns}}.json
 */
function initialize(): Promise<void> {
    if (_initializationPromise) {
        return _initializationPromise;
    }

    _enqueueLoad();

    const contentVersion = (window as any).ContentVersion || '1?v=${contentVersion}';

    _initializationPromise = new Promise((resolve) => {
        i18next
            .use(HttpBackend)
            .init({
                lng: _localeData.lng,
                fallbackLng: 'en-US',
                load: 'currentOnly',
                ns: ['common'],
                defaultNS: 'common',
                backend: {
                    loadPath: (lngs: string | string[], namespaces: string | string[]) => getLocaleLoadPath(lngs, namespaces, contentVersion),
                },
                interpolation: {
                    escapeValue: false,
                },
            }, (_err, _t) => {
                _hasInitialized = true;
                // Execute queued functions
                for (const fn of _functionStack) {
                    fn();
                }
                _functionStack.length = 0;
                _loadFinished();

                // Queued namespace loads start above, after i18next's base
                // initialization has completed. Do not report initialization
                // ready until those namespaces have finished as well, or the
                // React tree can render their untranslated keys.
                loadedOnce(resolve);
            });
    });

    return _initializationPromise;
}

function t(key: string, varObj?: any): string {
    return i18next.t(key, varObj) as string;
}

function addLanguage(namespace: string, callback?: () => void) {
    _namespaces.add(namespace);

    const doLoad = () => {
        _enqueueLoad();
        i18next.loadNamespaces(namespace, () => {
            _loadFinished();
            if (callback) callback();
        });
    };

    if (_hasInitialized) {
        doLoad();
    } else {
        _functionStack.push(doLoad);
    }
}

function getCurrentLanguage(): string {
    let lng = typeof i18next.language === 'string' ? i18next.language : null;
    if (!lng && _localeData && _localeData.lng) {
        lng = _localeData.lng;
    }

    return lng || '';
}

function getSyncfusionLocale(): string {
    const lng = getCurrentLanguage().toLowerCase();
    switch (lng) {
        case 'zh-hans': return 'zh-CN';
        case 'ja-jp':   return 'ja-JP';
        default:         return lng;
    }
}

function getSyncfusionJS2Locale(): string {
    const lng = getCurrentLanguage().toLowerCase();
    switch (lng) {
        case 'zh-hans': return 'zh';
        case 'ja-jp':   return 'ja';
        default:         return '';
    }
}

function getSyncfusionJS2JsonUrl(): string | null {
    const locale = getSyncfusionJS2Locale();
    if (locale) {
        const urlRoot = getStaticAssetRoot();
        return `${urlRoot}/locale/ej2-locale/${locale}.json`;
    }
    return null;
}

function getPdfTrueTypeFontUrl(): string | null {
    const lng = getCurrentLanguage().toLowerCase();
    let fontLng: string | null = null;
    switch (lng) {
        case 'zh-hans': fontLng = 'SC'; break;
        case 'ja-jp':   fontLng = 'JP'; break;
        default:         fontLng = '';
    }

    if (fontLng !== null) {
        const urlRoot = getStaticAssetRoot();
        return `${urlRoot}/locale/fonts/NotoSans${fontLng}-Regular.ttf`;
    }
    return null;
}

function getCldrJsonUrls(): string[] | null {
    const locale = getSyncfusionJS2Locale();
    if (locale) {
        const urlRoot = getStaticAssetRoot();
        return [
            `${urlRoot}/locale/ej2-locale/cldr-data/${locale}/ca-gregorian.json`,
            `${urlRoot}/locale/ej2-locale/cldr-data/${locale}/numbers.json`,
            `${urlRoot}/locale/ej2-locale/cldr-data/${locale}/timeZoneNames.json`,
            `${urlRoot}/locale/ej2-locale/cldr-data/numberingSystems.json`,
            `${urlRoot}/locale/ej2-locale/cldr-data/weekData.json`,
            `${urlRoot}/locale/ej2-locale/cldr-data/${locale}/currencies.json`,
        ];
    }
    return null;
}

function loadStyleSheet(id: string, href: string, callback?: () => void) {
    const existing = document.getElementById(id);
    if (!existing) {
        const link = document.createElement('link');
        link.type = 'text/css';
        link.rel = 'stylesheet';
        link.href = href;
        link.id = id;
        document.head.appendChild(link);
        link.onload = () => { if (callback) callback(); };
    } else {
        if (callback) callback();
    }
}

function unloadStyleSheet(id: string) {
    const link = document.getElementById(id);
    if (link && link.parentNode) {
        link.parentNode.removeChild(link);
    }
}

function loadSyncfusionJS1(callback?: () => void) {
    const urlRoot = getStaticAssetRoot();
    loadStyleSheet('ej.web.all.min.css', urlRoot + '/js/lib/js1/themes/default-theme/ej.web.all.min.css', callback);
}

function unloadSyncfusionJS1() {
    unloadStyleSheet('ej.web.all.min.css');
}

function loadedOnce(callback: () => void) {
    if (_loadStack.length > 0) {
        const token = PubSub.subscribe(_LOADED, () => {
            PubSub.unsubscribe(token);
            callback();
        });
    } else {
        callback();
    }
}

function isLoaded() {
    return _loadStack.length === 0;
}

function isLoading() {
    return _loadStack.length > 0;
}

function getHelpUrl(helpName: string): string {
    const helpBaseUrl = (window as any).HELP_URL || '';
    return `${helpBaseUrl}/${helpName.split('-').join('')}.html`;
}

function updateHelp(helpName: string) {
    const el = document.getElementById('ihdr-help-for-page');
    if (el) {
        el.setAttribute('href', getHelpUrl(helpName));
    }
}

// ── Singleton object exposed as window.InovxLoader ──────────────────
const InovxLoader = {
    initialize,
    subscribe: (callback: any) => PubSub.subscribe(_CHANGE, callback),
    unsubscribe: (callback: any) => { PubSub.unsubscribe(callback); },
    loaded: (callback: any) => PubSub.subscribe(_LOADED, callback),
    unload: (callback: any) => { PubSub.unsubscribe(callback); },
    loading: (callback: any) => PubSub.subscribe(_LOADING, callback),
    unloading: (callback: any) => { PubSub.unsubscribe(callback); },
    loadedOnce,
    get isLoaded() { return isLoaded(); },
    get isLoading() { return isLoading(); },
    t,
    addLanguage,
    getCurrentLanguage,
    getSyncfusionLocale,
    getSyncfusionJS2Locale,
    getSyncfusionJS2JsonUrl,
    getCldrJsonUrls,
    getPdfTrueTypeFontUrl,
    setLocaleData,
    getLocaleData,
    loadSyncfusionJS1,
    unloadSyncfusionJS1,
    loadStyleSheet,
    unloadStyleSheet,
    getHelpUrl,
    updateHelp,
};

// Attach to window so all legacy code can access it as a global
(window as any).InovxLoader = InovxLoader;

export default InovxLoader;
